"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Droplets, ShieldCheck, Sparkles, ArrowRight, CircleCheck as CheckCircle2, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  id: 1,
  title: "Lavagem de tanques e reatores",
  desc: "Cabeçotes rotativos mecânicos e hidráulicos de alto impacto."
}, {
  id: 2,
  title: "Higienização automatizada CIP",
  desc: "Redução comprovada no tempo de ciclo e no consumo de água."
}, {
  id: 3,
  title: "Certificações sanitárias",
  desc: "Soluções conformes com normas 3-A, EHEDG e FDA."
}];
export default function HeroCleaning() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  return <section id="herocleaningservices" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28 border-b border-border">
      {/* Background Decorative Gradient */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 right-0 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute top-1/2 -left-20 h-80 w-80 rounded-full bg-primary/5 blur-2xl" />
        <div className="absolute bottom-0 right-1/3 h-64 w-64 rounded-full bg-accent/20 blur-2xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column: Content */}
          <motion.div className="lg:col-span-7 flex flex-col items-start" initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={fadeIn}>
            <div className="inline-flex items-center gap-2 mb-4">
              <Badge variant="outline" className="px-3 py-1 text-xs font-semibold bg-accent text-accent-foreground border-primary/20">
                <Sparkles className="size-3.5 text-secondary" />
                <span>Spraying Systems Co. — Tecnologia de Ponta</span>
              </Badge>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight leading-tight">Limpeza &amp; Lavagem Industrial</h1>

            <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">Tecnologia de pulverização concebida para remover sujidades, resíduos e contaminantes, reduzindo simultaneamente o consumo de água, de produtos químicos e de mão de obra. Desde operações sanitárias de fabrico alimentar até ao fabrico de veículos, dispomos dos sistemas de pulverização adequados às suas necessidades.</p>

            {/* Key benefits list */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
              {features.map((item, index) => <div key={item.id} data-index={index} className="p-4 rounded-xl bg-card border border-border/80 shadow-xs flex flex-col justify-between">
                  <div className="flex items-center gap-2 mb-2 text-primary font-medium text-sm">
                    <CheckCircle2 className="size-4 text-secondary shrink-0" />
                    <span className="font-semibold text-foreground">{item.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {item.desc}
                  </p>
                </div>)}
            </div>

            {/* Action buttons */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-medium">
                <Link to="/TankCleaningPage">
                  <span>Ver lavagem de tanques</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted font-medium">
                <Link to="/CatalogsPage">
                  <FileText className="size-4 text-secondary" />
                  <span>Catálogos técnicos</span>
                </Link>
              </Button>
            </div>

            {/* Trust badge */}
            <div className="mt-8 pt-6 border-t border-border w-full flex items-center gap-3 text-xs text-muted-foreground">
              <ShieldCheck className="size-5 text-secondary shrink-0" />
              <span>Garantia de conformidade técnica e suporte especializado Eurocontrol em Portugal desde 1993.</span>
            </div>
          </motion.div>

          {/* Right Column: Visual Showcase */}
          <motion.div className="lg:col-span-5 relative" initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={fadeIn}>
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Outer card enclosure */}
              <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-lg">
                <div className="relative h-72 sm:h-84 lg:h-96 w-full overflow-hidden bg-muted">
                  <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_tank_cleaning_nozzle.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_tank_cleaning_nozzle.webp-wvc-sizes" alt="Cabeçote rotativo de limpeza e lavagem de tanques industriais" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" src="https://eurocontrol.pt/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-06-as-16.37.27.png" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />
                  
                  {/* Floating badge inside image */}
                  <div className="absolute bottom-4 left-4 right-4 text-background">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-secondary">
                      <Droplets className="size-4" />
                      <span>Eficiência Hídrica e CIP</span>
                    </div>
                    <p className="mt-1 text-sm font-medium text-background">
                      Sistemas de lavagem TankJet para recipientes até 30 metros de diâmetro.
                    </p>
                  </div>
                </div>

                {/* Sub-panel stats */}
                <div className="p-5 bg-card grid grid-cols-2 gap-4 divide-x divide-border">
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold text-primary font-default">-40%</span>
                    <span className="text-xs text-muted-foreground mt-0.5">Consumo médio de água</span>
                  </div>
                  <div className="pl-4 flex flex-col">
                    <span className="text-2xl font-bold text-secondary font-default">360°</span>
                    <span className="text-xs text-muted-foreground mt-0.5">Cobertura de lavagem</span>
                  </div>
                </div>
              </div>

              {/* Decorative Corner Accent */}
              <div className="absolute -bottom-3 -right-3 size-20 rounded-xl bg-secondary/15 -z-10" />
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}